import { useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { PageProps } from '@/types'
import AppLayout from '@/layouts/AppLayout'
import { MailCheck, Send, AlertTriangle, Info, Loader2, Download } from 'lucide-react'

interface SystemEmail {
  key: string
  name: string
  description: string
  trigger: string
  source: 'built_in' | 'template'
  template_key: string | null
  template_installed: boolean
  uses_fallback_copy: boolean
  has_automatic_trigger: boolean
  sendable: boolean
  unavailable_reason: string | null
}

interface Props extends PageProps {
  emails: SystemEmail[]
  default_recipient: string
  sendgrid: { ready: boolean; blocking_reason: string | null; sender: string | null }
  missing_default_templates: string[]
}

export default function EmailTestsIndex({ emails, default_recipient, sendgrid, missing_default_templates }: Props) {
  const [recipient, setRecipient] = useState(default_recipient ?? '')
  const [sending, setSending] = useState<string | null>(null)
  const [installing, setInstalling] = useState(false)

  // Same action the Workflows page uses; it returns here with the result so the
  // admin never has to go hunting for the fix.
  const installDefaults = () => {
    setInstalling(true)
    router.post(
      '/settings/workflows/templates/install_defaults',
      { return_to: 'email_tests' },
      { preserveScroll: true, onFinish: () => setInstalling(false) }
    )
  }

  const send = (key: string) => {
    setSending(key)
    router.post(
      '/admin/email_tests',
      { key, to: recipient },
      { preserveScroll: true, onFinish: () => setSending(null) }
    )
  }

  const canSend = sendgrid.ready && recipient.trim().length > 0

  return (
    <AppLayout>
      <Head title="Email Tests" />

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-chestnut-charcoal font-heading flex items-center gap-3">
            <MailCheck className="h-7 w-7 text-chestnut-forest" />
            Email Tests
          </h1>
          <p className="text-chestnut-charcoal/70 mt-1">
            Send a real copy of any system email to an address you choose. Test sends use the same
            SendGrid credentials, sender identity and delivery path as the real thing — but every
            name, password and link inside them is sample data, and no account is touched.
          </p>
        </div>

        {!sendgrid.ready && (
          <div className="flex items-start gap-2 p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-800">
            <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Email cannot be sent right now</p>
              <p className="mt-1">{sendgrid.blocking_reason}</p>
              <Link href="/settings/integrations" className="underline mt-1 inline-block">
                Go to Integrations
              </Link>
            </div>
          </div>
        )}

        {missing_default_templates.length > 0 && (
          <div className="flex items-start gap-2 p-4 rounded-xl bg-blue-50 border border-blue-200 text-blue-900">
            <Info className="h-5 w-5 shrink-0 mt-0.5" />
            <div className="text-sm flex-1">
              <p className="font-medium">
                {missing_default_templates.length} default template
                {missing_default_templates.length === 1 ? ' is' : 's are'} not installed for this organization
              </p>
              <p className="mt-1">
                Missing: {missing_default_templates.join(', ')}. Until then, emails with built-in copy
                fall back to it and template-only emails cannot be tested.
              </p>
              <p className="mt-1">
                Installing adds only what is missing — templates you have already customized are left
                exactly as they are. You can also manage them from{' '}
                <Link href="/settings/workflows?tab=email" className="underline">
                  Settings → Workflows
                </Link>
                .
              </p>
              <button
                type="button"
                onClick={installDefaults}
                disabled={installing}
                className="btn-secondary flex items-center gap-2 mt-3 disabled:opacity-40"
              >
                {installing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {installing ? 'Installing…' : 'Install default templates'}
              </button>
            </div>
          </div>
        )}

        <div className="card p-4 space-y-3">
          <label className="block text-sm font-medium text-chestnut-charcoal" htmlFor="test-recipient">
            Send test copies to
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <input
              id="test-recipient"
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="you@yourdomain.com"
              className="px-3 py-2 text-sm border border-chestnut-charcoal/15 rounded-lg w-80 focus:outline-none focus:border-chestnut-forest"
            />
            {sendgrid.sender && (
              <span className="text-sm text-chestnut-charcoal/60">Sent from {sendgrid.sender}</span>
            )}
          </div>
          <p className="text-sm text-chestnut-charcoal/60">
            Every test send is recorded in{' '}
            <Link href="/admin/email_activity?source=test" className="underline">
              Email Activity
            </Link>{' '}
            with its status or the provider's failure reason.
          </p>
        </div>

        <div className="card divide-y divide-chestnut-charcoal/5">
          {emails.map((email) => (
            <div key={email.key} className="p-4 flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-[18rem]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-chestnut-charcoal">{email.name}</span>
                  {email.template_key && (
                    <code className="text-xs px-2 py-0.5 bg-chestnut-cream rounded">{email.template_key}</code>
                  )}
                  {!email.has_automatic_trigger && (
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
                      No automatic trigger
                    </span>
                  )}
                  {email.uses_fallback_copy && (
                    <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                      Using built-in copy
                    </span>
                  )}
                </div>
                <p className="text-sm text-chestnut-charcoal/70 mt-1">{email.description}</p>
                <p className="text-sm text-chestnut-charcoal/50 mt-1">{email.trigger}</p>
                {email.unavailable_reason && (
                  <p className="text-sm text-amber-700 mt-1">{email.unavailable_reason}</p>
                )}
              </div>

              <button
                type="button"
                onClick={() => send(email.key)}
                disabled={!canSend || !email.sendable || sending !== null}
                className="btn-secondary flex items-center gap-2 disabled:opacity-40"
              >
                {sending === email.key ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {sending === email.key ? 'Sending…' : 'Send test'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </AppLayout>
  )
}
